.Page {
  --marketing-navy: #1f2544;
  --marketing-blue: #1668c4;
  --marketing-blue-light: #eaf3fc;
  --marketing-orange: #d9701e;
  --marketing-orange-light: #fdf0e4;
  color: var(--marketing-navy);
  font-size: 1rem;
  line-height: 1.65;
}

.Page *,
.Page *::before,
.Page *::after {
  box-sizing: border-box;
}

.Section {
  padding: clamp(3.5rem, 8vw, 7rem) clamp(1.25rem, 5vw, 5rem);
}

.Section:nth-child(even) {
  background: #f7fafc;
}

.SectionInner {
  max-width: 80rem;
  margin: 0 auto;
}

.Narrow {
  max-width: 48rem;
}

.Hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(2rem, 6vw, 5rem);
  align-items: center;
  max-width: 86rem;
  min-height: 36rem;
  margin: 0 auto;
  padding: clamp(1rem, 3vh, 3rem) clamp(1.25rem, 5vw, 5rem);
  overflow: hidden;
}

.HeroCopy {
  min-width: 0;
}

.HeroVisual {
  min-width: 0;
}

.Title {
  max-width: 15ch;
  margin: 0;
  font-size: clamp(2.7rem, 6vw, 5.5rem);
  font-weight: 800;
  letter-spacing: -0.055em;
  line-height: 0.98;
}

.Heading {
  max-width: 22ch;
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.08;
}

.Subheading {
  max-width: 46rem;
  margin: 1.25rem 0 0;
  color: #526078;
  font-size: clamp(1.05rem, 1.8vw, 1.3rem);
}

.Intro {
  max-width: 48rem;
  margin: 1rem 0 0;
  color: #526078;
  font-size: 1.08rem;
}

.Actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;
}

.Button,
.ButtonSecondary {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  border: 1px solid var(--marketing-blue);
  border-radius: 999px;
  font-weight: 750;
  text-decoration: none;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.Button {
  background: var(--marketing-blue);
  color: #fff;
}

.ButtonSecondary {
  background: #fff;
  color: var(--marketing-blue);
}

.Button:hover,
.ButtonSecondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px -18px rgba(31, 37, 68, 0.75);
}

.Button:hover {
  color: #fff;
}

.Page .ButtonSecondary:hover,
.Page .ButtonSecondary:focus-visible {
  background: #fff;
  color: #1668c4;
}

.Proof {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-top: 2rem;
  color: #526078;
}

.Avatars {
  display: flex;
}

.Avatar {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  margin-left: -0.5rem;
  place-items: center;
  border: 3px solid #fff;
  border-radius: 50%;
  background: var(--marketing-blue-light);
  color: var(--marketing-blue);
  font-size: 0.75rem;
  font-weight: 800;
}

.Avatar:first-child {
  margin-left: 0;
}

.Grid2,
.Grid3,
.Grid4 {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
  margin-top: 2.5rem;
}

.Card {
  padding: clamp(1.25rem, 3vw, 2rem);
  border: 1px solid #dbe4ef;
  border-radius: 1.25rem;
  background: #fff;
  box-shadow: 0 20px 45px -40px rgba(31, 37, 68, 0.65);
}

.Card h3,
.Card h4 {
  margin: 0 0 0.5rem;
  font-size: 1.2rem;
}

.Card p {
  margin: 0;
  color: #526078;
}

.TestimonialCard {
  display: flex;
  min-height: 100%;
  margin: 0;
  padding: clamp(1.5rem, 3vw, 2rem);
  flex-direction: column;
  border: 1px solid #dbe4ef;
  border-radius: 1.25rem;
  background: #fff;
  box-shadow: 0 20px 45px -40px rgba(31, 37, 68, 0.65);
}

.TestimonialQuoteIcon {
  display: block;
  width: 3rem;
  height: 2.25rem;
  margin-bottom: 1.5rem;
}

.TestimonialCard blockquote {
  margin: 0 0 2rem;
  color: var(--marketing-navy);
  font-size: 1.08rem;
  font-weight: 650;
  line-height: 1.6;
}

.TestimonialCard figcaption {
  display: grid;
  gap: 0.2rem;
  margin-top: auto;
}

.TestimonialCard figcaption strong {
  color: var(--marketing-navy);
}

.TestimonialCard figcaption span {
  color: #526078;
  font-size: 0.92rem;
  line-height: 1.45;
}

.IconBadge,
.StepNumber {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  margin-bottom: 1rem;
  place-items: center;
  border-radius: 0.85rem;
  background: var(--marketing-blue-light);
  color: var(--marketing-blue);
  font-weight: 800;
}

.Placeholder {
  margin-top: 1rem;
  padding: 0.8rem 1rem;
  border: 1px dashed #c06b20;
  border-radius: 0.75rem;
  background: #fff8ef;
  color: #7a4214;
  font-size: 0.9rem;
}

.Tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2rem 0 1rem;
}

.Tab {
  flex: 0 1 auto;
  max-width: 100%;
  padding: 0.65rem 0.9rem;
  border: 1px solid #dbe4ef;
  border-radius: 999px;
  background: #fff;
  color: var(--marketing-navy);
  font-weight: 700;
  cursor: pointer;
}

.TabActive {
  border-color: var(--marketing-blue);
  background: var(--marketing-blue);
  color: #fff;
}

.FeaturePanel {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
  padding: clamp(1.5rem, 4vw, 3rem);
  border-radius: 1.5rem;
  background: var(--marketing-navy);
  color: #fff;
}

.FeaturePanel p {
  color: #dce8f7;
}

.FeaturePanel ul {
  padding-left: 1.2rem;
}

.Sample {
  align-self: start;
  padding: 1.25rem;
  border-radius: 1rem;
  background: #fff;
  color: var(--marketing-navy);
}

.Sample h3 {
  margin-top: 0;
}

.TableWrap {
  margin-top: 1.5rem;
  overflow-x: auto;
}

.Table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.Table th,
.Table td {
  padding: 0.75rem;
  border-bottom: 1px solid #dbe4ef;
  text-align: left;
  vertical-align: top;
}

.Table th {
  color: #526078;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.Faq {
  border-bottom: 1px solid #dbe4ef;
}

.Faq summary {
  padding: 1.2rem 2rem 1.2rem 0;
  cursor: pointer;
  font-weight: 750;
}

.Faq p {
  margin: 0 0 1.25rem;
  color: #526078;
}

.CtaBand {
  padding: clamp(2rem, 5vw, 4rem);
  border-radius: 1.5rem;
  background: var(--marketing-navy);
  color: #fff;
}

.CtaBand p {
  max-width: 46rem;
  color: #dce8f7;
}

.CtaEyebrow {
  display: inline-flex;
  margin-bottom: 1.25rem;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  background: var(--marketing-orange);
  color: var(--marketing-navy);
  font-size: 0.9rem;
  font-weight: 750;
}

.CtaBandSplit {
  display: grid;
  gap: clamp(2rem, 5vw, 4rem);
}

.CtaChecklist {
  display: grid;
  gap: 0.75rem;
  margin: 2rem 0 0;
  padding-left: 1.25rem;
}

.CtaBandForm {
  padding: clamp(1.25rem, 3vw, 2rem);
  border-radius: 1.25rem;
  background: #fff;
  color: var(--marketing-navy);
}

.CtaBandForm h3 {
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
}

.CtaBandForm .Form {
  margin-top: 1.25rem;
}

.CtaBandForm p {
  color: #526078;
}

.Form {
  display: grid;
  gap: 1rem;
  margin-top: 2rem;
}

.FormGrid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
}

.Field {
  display: grid;
  gap: 0.35rem;
  font-weight: 700;
}

.Field input,
.Field select,
.Field textarea {
  width: 100%;
  padding: 0.8rem 0.9rem;
  border: 1px solid #b9c7d8;
  border-radius: 0.65rem;
  background: #fff;
  color: var(--marketing-navy);
  font: inherit;
}

.Field textarea {
  min-height: 9rem;
  resize: vertical;
}

.Pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.Pill {
  padding: 0.55rem 0.8rem;
  border: 1px solid #b9c7d8;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
}

.PillActive {
  border-color: var(--marketing-blue);
  background: var(--marketing-blue-light);
  color: var(--marketing-blue);
  font-weight: 750;
}

.Status {
  min-height: 1.5rem;
  margin: 0;
  font-weight: 700;
}

.AnchorNav {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.AnchorNav a {
  color: var(--marketing-blue);
  font-weight: 700;
}

.Video {
  display: block;
  position: relative;
  min-height: 20rem;
  overflow: hidden;
  border-radius: 1.5rem;
  background: linear-gradient(145deg, var(--marketing-blue-light), #bfdcf7);
  color: var(--marketing-navy);
  text-decoration: none;
  box-shadow: 0 28px 70px -45px rgba(31, 37, 68, 0.8);
}

.VideoContent {
  position: absolute;
  right: 1.5rem;
  bottom: 1.5rem;
  left: 1.5rem;
  padding: 1.25rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.92);
}

.Play {
  display: grid;
  width: 4rem;
  height: 4rem;
  margin-bottom: 1rem;
  place-items: center;
  border-radius: 50%;
  background: var(--marketing-blue);
  color: #fff;
  font-size: 1.5rem;
}

@media (min-width: 700px) {
  .Grid2,
  .FormGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .Grid3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .Grid4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .FeaturePanel {
    grid-template-columns: minmax(0, 3fr) minmax(18rem, 2fr);
  }
}

@media (min-width: 900px) {
  .Hero {
    grid-template-columns: minmax(0, 1fr) minmax(24rem, 0.9fr);
  }

  .CtaBandSplit {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }

  .Grid4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 699px) {
  .HeroVisual {
    transform: scale(0.78);
    transform-origin: top left;
    width: 128%;
    margin-bottom: -6rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .Button,
  .ButtonSecondary {
    transition: none;
  }
}
